
import Vue from 'vue';
import pluralize from 'pluralize';
import { mapGetters, mapState } from 'vuex';
import { CompanyUserRoleDef } from '@run-diffusion/shared';
import { db } from '@/firebase';
import { titleCase } from '@/filters';
import { get$bindFirestoreOptions, TeamUserMixin } from '@/mixins';
import { ROUTER } from '@/router/constants';
import { MemberRow, MemberTeamRef } from '@/utils/membersApi';
import {
	FailedTeamIdsMap,
	KEYED_BIND_OPTIONS,
	MemberCompanyRoleReport,
	MemberDisplay,
	MemberJoinedState,
	MemberRowState,
	MembershipsMap,
	boundMemberTeamIds,
	failedTeamIdsAfterBindFailure,
	memberDashboardTeamId,
	memberDisplay,
	memberJoinedState,
	memberRowState,
	removableMemberTeams,
} from './membersDirectoryState';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseStyledMenu from '@/components/base/BaseStyledMenu.vue';
import ClipboardCopy from '@/components/ClipboardCopy.vue';
import MemberTeamsTableCell from './MemberTeamsTableCell.vue';
import MemberCompanyRoleTableCell from './MemberCompanyRoleTableCell.vue';

/**
 * One person in the directory. The Postgres row says which teams to bind;
 * the `teams/{teamId}/teamMembers/{email}` docs of the first
 * MEMBERS_DIRECTORY_MAX_TEAM_CHIPS teams (the only ones that get a chip) are
 * bound into `memberships` so the row goes live, and it shows as removed once
 * every membership it names is known to be gone and they hold no company
 * role. A person on no team lives by their company role doc instead, and the
 * company-role cell stays mounted (hidden) in the removed state so that doc is
 * always watched. The deprecated `TeamMember.role` is never read here.
 */
export default Vue.extend({
	name: 'MemberDirectoryRow',
	mixins: [
		TeamUserMixin,
	],
	props: {
		row: {
			type: Object as () => MemberRow,
			required: true,
		},
		// Added on this page and shown ahead of the listing (utils/justAdded.ts)
		justAdded: { type: Boolean, default: false },
		companyId: {
			type: String,
			default: null,
		},
		companyUserRoleDefsMap: {
			type: Object as () => Record<string, CompanyUserRoleDef>,
			default: () => ({}),
		},
		// Whether the page shows company roles: the Company Role column, and the role binding behind it
		showCompanyRole: {
			type: Boolean,
			default: false,
		},
		// Every column, the checkbox column included
		colspan: {
			type: Number,
			default: 6,
		},
	},
	data () {
		return {
			// teamId -> undefined (binding), null (doc does not exist), or the doc
			memberships: {} as MembershipsMap,
			// teamId -> true for a membership bind that was refused or failed. A
			// rejected bind writes nothing, so this is what stops the wait.
			failedTeamIds: {} as FailedTeamIdsMap,
			boundEmail: null as string | null,
			// What the company-role cell resolved live: undefined until it reports.
			liveCompanyRoleDefId: undefined as string | null | undefined,

			MORE_ACTIONS_ITEM_VALUE: {
				ADD_COMPANY_ROLE: 'add_company_role',
				EDIT_COMPANY_ROLE: 'edit_company_role',
				REMOVE_COMPANY_ROLE: 'remove_company_role',
				REMOVE_FROM_ALL_TEAMS: 'remove_from_all_teams',
			},
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
		]),
		...mapGetters([
			'displayLabels',
		]),
		// Only the teams that can get a chip are watched, never the whole list.
		boundTeamIds (): string[] {
			return boundMemberTeamIds(this.row);
		},
		// One string that changes whenever the set of docs to bind changes.
		bindingSignature (): string {
			return `${this.row?.email || ''}|${this.boundTeamIds.join(',')}`;
		},
		rowState (): MemberRowState {
			return memberRowState(this.memberships, this.boundTeamIds, {
				totalTeams: (this.row?.membershipTeams || []).length,
				failedTeamIds: this.failedTeamIds,
				onNoTeam: !!this.row?.onNoTeam,
				liveCompanyRoleDefId: this.liveCompanyRoleDefId,
				// A row kept after its last team because of a company role would show that role
				hasCompanyRole: this.showCompanyRole && !!this.row?.hasCompanyRole,
			});
		},
		display (): MemberDisplay {
			return memberDisplay(this.row, this.memberships, this.boundTeamIds);
		},
		joinedState (): MemberJoinedState {
			return memberJoinedState(this.display);
		},
		isSelfMember (): boolean {
			return !!(this.user?.id && this.row?.userId && this.user.id === this.row.userId);
		},
		companyUserRoleDefId (): string | null {
			if (this.liveCompanyRoleDefId !== undefined) return this.liveCompanyRoleDefId;
			return this.display.companyUserRoleDefIdFromStub || null;
		},
		hasCompanyRole (): boolean {
			if (this.liveCompanyRoleDefId !== undefined) return !!this.liveCompanyRoleDefId;
			return !!this.display.companyUserRoleDefIdFromStub || !!this.row?.hasCompanyRole;
		},
		canManageCompanyRole (): boolean {
			return !!(
				this.hasManageCompanyPermission &&
				!this.scimProvisioningLocksMembership &&
				(this.user?.isAdmin || !this.isSelfMember)
			);
		},
		// What the page is told, for its bulk actions and its CSV
		companyRoleReport (): MemberCompanyRoleReport {
			return {
				companyUserRoleDefId: this.companyUserRoleDefId,
				hasCompanyRole: this.hasCompanyRole,
			};
		},
		removableTeams (): MemberTeamRef[] {
			return removableMemberTeams(this.row, this.memberships);
		},
		// Every team, so the company-wide MANAGE_TEAMS (a team's own permission reaches only that
		// team). Never themselves, which TeamPage refuses for anyone, RunDiffusion admins included.
		canRemoveFromAllTeams (): boolean {
			return !!(
				this.hasManageTeamsPermission &&
				!this.scimProvisioningLocksMembership &&
				!this.isSelfMember &&
				this.removableTeams.length
			);
		},
		// The team whose member dashboard the address opens, or null for none.
		dashboardTeamId (): string | null {
			return memberDashboardTeamId(this.row, this.memberships, {
				currentTeamId: this.team?.id || null,
				canOpenAnyTeam: !!(this.hasManageTeamsPermission || this.user?.isAdmin),
			});
		},
		moreActionsMenuItems () {
			let idIncrementer: number = 0;
			return [
				...(this.canManageCompanyRole ? (this.hasCompanyRole ? [
					{
						id: idIncrementer++,
						icon: 'mdi-shield-account-outline',
						label: 'Update Company Role',
						description: 'Change their company role',
						value: this.MORE_ACTIONS_ITEM_VALUE.EDIT_COMPANY_ROLE,
					},
					{
						id: idIncrementer++,
						icon: 'mdi-minus',
						iconStyle: { color: 'var(--red-500)' },
						descriptionStyle: { color: 'var(--grey-400)' },
						label: 'Remove Company Role',
						description: 'They will lose company admin access',
						value: this.MORE_ACTIONS_ITEM_VALUE.REMOVE_COMPANY_ROLE,
					},
				] : [
					{
						id: idIncrementer++,
						icon: 'mdi-shield-plus-outline',
						label: 'Assign Company Role',
						description: 'Grant them a company role',
						value: this.MORE_ACTIONS_ITEM_VALUE.ADD_COMPANY_ROLE,
					},
				]) : []),
				...(this.canRemoveFromAllTeams ? [
					{
						id: idIncrementer++,
						icon: 'mdi-account-minus-outline',
						iconStyle: { color: 'var(--red-500)' },
						descriptionStyle: { color: 'var(--grey-400)' },
						label: `Remove from all ${titleCase(pluralize(this.displayLabels.team))}`,
						description: `Takes them off every ${this.displayLabels.team} they are on`,
						value: this.MORE_ACTIONS_ITEM_VALUE.REMOVE_FROM_ALL_TEAMS,
					},
				] : []),
			];
		},
	},
	watch: {
		bindingSignature: {
			immediate: true,
			handler () {
				this.syncBindings();
			},
		},
		companyRoleReport: {
			immediate: true,
			handler (report: MemberCompanyRoleReport) {
				this.$emit('company-role', { key: this.row?.key, ...report });
			},
		},
		// Once, on the way in, as a Teams table's row does: the page asks again after the mirror's lag
		rowState (newVal: MemberRowState, oldVal: MemberRowState) {
			if (newVal === 'removed' && oldVal !== 'removed') this.$emit('removed', this.row?.key);
		},
	},
	methods: {
		membershipKey (teamId: string): string {
			return `memberships.${teamId}`;
		},
		/**
		 * Reconcile the bound docs with the row's team list: unbind the teams
		 * that left, keep the ones that stayed, and bind the newcomers. An email
		 * change re-keys every doc, so everything is rebound.
		 */
		syncBindings () {
			const email: string = this.row?.email || '';
			const desired: string[] = email ? this.boundTeamIds : [];
			const emailChanged: boolean = this.boundEmail !== email;
			const bound: string[] = Object.keys(this.memberships);

			const departed: string[] = bound.filter((teamId) => emailChanged || !desired.includes(teamId));
			for (const teamId of departed) {
				const key: string = this.membershipKey(teamId);
				if (this.$firestoreRefs?.[key]) this.$unbind(key, false);
			}

			const next: MembershipsMap = {};
			const nextFailed: FailedTeamIdsMap = {};
			const toBind: string[] = [];
			for (const teamId of desired) {
				const keep: boolean = !emailChanged && bound.includes(teamId);
				next[teamId] = keep ? this.memberships[teamId] : undefined;
				// A kept team is not rebound, so a failure it had still stands.
				if (keep && this.failedTeamIds[teamId]) nextFailed[teamId] = true;
				if (!keep) toBind.push(teamId);
			}
			// Replace the whole map so every key is reactive before vuefire
			// writes into it (walkSet assigns in place, which Vue 2 cannot see
			// for a key that did not exist).
			this.memberships = next;
			this.failedTeamIds = nextFailed;
			this.boundEmail = email;

			for (const teamId of toBind) {
				this.bindMembership(teamId, email);
			}
		},
		async bindMembership (teamId: string, email: string) {
			try {
				await this.$bind(
					this.membershipKey(teamId),
					db.doc(`teams/${teamId}/teamMembers/${email}`),
					// maxRefDepth: 0 because users don't have access to other users' document data.
					// KEYED_BIND_OPTIONS because a dotted key cannot take the default wait: true.
					get$bindFirestoreOptions({ ...KEYED_BIND_OPTIONS, maxRefDepth: 0 }),
				);
			} catch (error) {
				// Membership docs are world-readable, so this is rare, but one
				// refused read must never strand the row: a rejected bind writes
				// nothing, so the key is recorded as failed and counts as
				// resolved. A failure for an email the row has moved on from is stale.
				console.error('Error binding team membership:', error);
				if (this.boundEmail !== email) return;
				this.failedTeamIds = failedTeamIdsAfterBindFailure(this.failedTeamIds, this.memberships, teamId);
			}
		},
		onBoundRoleDefId (roleDefId: string | null | undefined) {
			this.liveCompanyRoleDefId = roleDefId;
		},
		onViewMember () {
			const teamId: string | null = this.dashboardTeamId;
			if (!teamId || !this.row?.email) return;
			this.routerPush(this.$route, this.$router, {
				name: ROUTER.TEAM_MEMBER_DASHBOARD,
				params: {
					teamId,
					// The membership doc's id. Raw: vue-router encodes params on push and decodes them on match.
					teamMemberId: this.row.email,
				},
				// Carried in as it is carried back out by the member dashboard's crumbs.
				query: this.$route.query,
			});
		},
		onSelectedMoreActions (moreActionsItemValue: string) {
			const payload = {
				row: this.row,
				email: this.row?.email || '',
				userId: this.row?.userId || null,
				companyUserRoleDefId: this.companyUserRoleDefId,
			};

			// TeamPage owns the dialogs for the events below, the way it does for every other membership action.
			switch (moreActionsItemValue) {
				case this.MORE_ACTIONS_ITEM_VALUE.ADD_COMPANY_ROLE:
					this.$emit('add-company-user-role', payload);
					break;
				case this.MORE_ACTIONS_ITEM_VALUE.EDIT_COMPANY_ROLE:
					this.$emit('edit-company-user-role', payload);
					break;
				case this.MORE_ACTIONS_ITEM_VALUE.REMOVE_COMPANY_ROLE:
					this.$emit('remove-company-user-role', payload);
					break;
				// Typed to confirm: it can take someone off many teams in one go.
				case this.MORE_ACTIONS_ITEM_VALUE.REMOVE_FROM_ALL_TEAMS:
					this.$emit('remove-member-from-teams', {
						teams: this.removableTeams,
						teamMember: { email: this.row?.email || '', userId: this.row?.userId || null },
						fromAllTeams: true,
					});
					break;
				default:
					break;
			}
		},
	},
	components: {
		BaseButton,
		BaseStyledMenu,
		ClipboardCopy,
		MemberTeamsTableCell,
		MemberCompanyRoleTableCell,
	},
});
